import type { createReportRequest } from '../requests/report.ts';

export type DuplicateReportPayload = Parameters<typeof createReportRequest>[0];

/** Metadata keys that describe the source's lifecycle rather than its content. */
const LIFECYCLE_METADATA_KEYS = [
  'scope',
  'publishedAt',
  'publishedCompanyId',
  'archivedAt',
  'export_tracking',
] as const;

// Report records are WarpDrive proxies, which structuredClone rejects; both
// fields are JSON columns, so a JSON round trip is lossless.
function jsonCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value ?? {})) as T;
}

function isConfigObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasCompanySelection(value: unknown) {
  return value !== undefined && value !== null && value !== '';
}

function queryHasCompanySelection(query: unknown): boolean {
  if (query === undefined || query === null || query === '') return false;
  if (typeof query === 'string') {
    return new URLSearchParams(query).getAll('companyId').some(hasCompanySelection);
  }
  return !isConfigObject(query) || hasCompanySelection(query['companyId']);
}

/**
 * Whether a saved report has no company selected in its persisted configuration.
 * Visibility, publication audiences and field names do not determine eligibility.
 * Legacy URL parameters may also retain the builder's company selection.
 *
 * @param source the accessible saved record, before applying unsaved builder changes.
 * @returns false for an unsaved record, missing or malformed configuration, or any
 *   companyId other than null, undefined or an empty string. This checks content
 *   eligibility only; callers must separately enforce access and creation permissions.
 */
export function canDuplicateReport(
  source: { id?: string | null; searchConfig?: unknown; metadata?: unknown } | null | undefined
): boolean {
  if (!source?.id?.trim() || !isConfigObject(source.searchConfig)) return false;
  if (hasCompanySelection(source.searchConfig['companyId'])) return false;
  if (queryHasCompanySelection(source.searchConfig['urlParams'])) return false;
  if (source.metadata === undefined || source.metadata === null) return true;
  if (!isConfigObject(source.metadata)) return false;
  return !queryHasCompanySelection(source.metadata['serializedQuery']);
}

/**
 * Builds the create-report payload for a copy of a saved report.
 *
 * The copy keeps the source's report type, description, search config and
 * content metadata (deep-copied, so editing the copy never touches the source).
 * It starts private and unpublished: publication, archive and export history
 * metadata are dropped, and `saved_at` is stamped. Staff copies use `admin_only`;
 * company-locked copies omit scope so the API's private-report ownership rule
 * lets their partner creator reopen them. Missing scope is unpublished in the
 * frontend, and private visibility excludes all partner publication audiences.
 * Schedules and the internal publish channel are not copied.
 *
 * @param source the saved report to copy.
 * @param options.companyId a positive decimal company id within the safe integer
 *   range locks partner access to that company; null, undefined and empty string
 *   create a staff copy with no partner access.
 * @param options.now the creation time, stamped as `metadata.saved_at`.
 * @returns the attributes for `createReportRequest`.
 * @throws if a nonempty company lock is not a positive safe decimal integer.
 */
export function buildDuplicateReportPayload(
  source: {
    name?: string | null;
    description?: string | null;
    reportType: string;
    searchConfig?: Record<string, unknown> | null;
    metadata?: Record<string, unknown> | null;
  },
  options: { companyId?: string | null; now?: Date } = {}
): DuplicateReportPayload {
  let companyId: number | null = null;
  if (options.companyId) {
    companyId = Number(options.companyId);
    if (!/^[1-9]\d*$/.test(options.companyId) || !Number.isSafeInteger(companyId)) {
      throw new Error('Cannot duplicate a report with an invalid company lock.');
    }
  }

  const metadata = jsonCopy(source.metadata ?? {});
  for (const key of LIFECYCLE_METADATA_KEYS) delete metadata[key];
  if (companyId === null) metadata['scope'] = 'admin_only';

  return {
    name: duplicateReportName(source.name),
    description: source.description ?? '',
    reportType: source.reportType,
    searchConfig: jsonCopy(source.searchConfig ?? {}),
    visibilityLevel: 'private',
    partnerAccessConfig: companyId ? [companyId] : [],
    metadata: {
      ...metadata,
      saved_at: (options.now ?? new Date()).toISOString(),
    },
  };
}

/**
 * The default name for a copy.
 *
 * @param name the source report's name; blank names fall back to "Untitled report".
 * @returns the name with " (Copy)" appended.
 */
export function duplicateReportName(name: string | null | undefined): string {
  return `${name?.trim() || 'Untitled report'} (Copy)`;
}
